/**
 * One fetched group: its value, where it came from, and who may change it
 * (ORDR-119, items 3, 4 and 8).
 *
 * THE SERVER'S SENTENCES ARE QUOTED, NOT COMPOSED. `explanation` is rendered
 * verbatim, "Locked." is added only when `mode` is `LOCKED`, and "Why?" lists
 * the explain walk's own notes. Nothing here writes a lock sentence of its own.
 *
 * FAIL CLOSED AND SAY SO. A failed GET is "Could not load" with the
 * correlation id, a Retry, and no edit control. A 404 is not a failure: it is
 * the merged GET's answer for a node outside the caller's scope (ORDR-135).
 */

import { useState, type ReactElement } from "react";
import type { ConfigApi, ConfigReading } from "./configApi.js";
import { editability } from "./capability.js";
import { NO_ORDERING_READER, type ConfigDomain } from "./domains.js";
import { Editor } from "./Editor.js";
import type { ConfigScope } from "./index.js";
import { originSentence } from "./provenance.js";
import { failureText } from "./refusal.js";
import type { GroupState } from "./useGroups.js";
import { ValueView } from "./ValueView.js";

export interface GroupCardProps {
  readonly testId: string;
  readonly label: string;
  readonly domain: ConfigDomain;
  readonly state: GroupState | undefined;
  readonly scope: ConfigScope;
  readonly api: ConfigApi;
  readonly keys?: (key: string) => boolean;
  readonly typedOnly?: boolean;
  readonly onRetry: () => void;
  readonly onSaved: (reading: ConfigReading) => void;
}

export function GroupCard(props: GroupCardProps): ReactElement {
  const { testId, label, domain, state, scope, api, keys = () => true } = props;
  const [editing, setEditing] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [why, setWhy] = useState<readonly string[] | string | null>(null);

  const box = "rounded border border-[var(--color-neutral-200)] p-2 text-sm";
  if (state === undefined || state.status === "loading") {
    return <li data-testid={testId} className={box}>Loading {label}...</li>;
  }
  if (state.status === "failed" && state.failure.status === 404) {
    // ORDR-135 (C-HQ6): a node outside the caller's scope is a 404 with the
    // same body as one that does not exist. A fact about scope, not a fault:
    // no alert, no reference, nothing to retry.
    return (
      <li data-testid={testId} className={box}>
        <p>{domain} is not available at this scope.</p>
      </li>
    );
  }
  if (state.status === "failed") {
    return (
      <li data-testid={testId} className={box}>
        <p role="alert">{failureText(domain, state.failure)}</p>
        <button type="button" onClick={props.onRetry} className="underline">
          Retry
        </button>
      </li>
    );
  }

  const { reading } = state;
  const gate = editability(domain, scope.capabilities, reading.writable, reading.explanation, scope.nodeName);
  const editable = gate.editable && !readOnly;

  const explain = async (): Promise<void> => {
    const outcome = await api.explain(scope.nodeId, domain);
    setWhy(outcome.ok ? outcome.notes : `Could not load why. Reference: ${outcome.correlationId}.`);
  };

  return (
    <li data-testid={testId} className={box}>
      <p className="font-medium">{label}</p>
      <p>{originSentence(reading, scope.nodeId, scope.nodeName)}</p>
      <p>
        {reading.explanation}
        {reading.mode === "LOCKED" && " Locked."}
      </p>
      <ValueView value={reading.value} keys={keys} />
      <p className="text-xs">As of {reading.resolvedAt}</p>
      {NO_ORDERING_READER.has(domain) && (
        <p>No ordering service reads this setting yet, so changing it has no effect on orders.</p>
      )}

      <button type="button" onClick={() => void explain()} className="underline">
        Why?
      </button>
      {typeof why === "string" && <p role="alert">{why}</p>}
      {Array.isArray(why) && (
        <ol className="list-decimal pl-5">
          {why.map((note, index) => (
            <li key={index}>{note}</li>
          ))}
        </ol>
      )}

      {notice !== null && <p role="alert" data-testid="group-notice">{notice}</p>}
      {!editable && gate.reason !== null && gate.reason !== reading.explanation && <p>{gate.reason}</p>}

      {editable && !editing && (
        <button type="button" onClick={() => setEditing(true)} className="block underline">
          Change
        </button>
      )}
      {editable && editing && (
        <Editor
          domain={domain}
          reading={reading}
          scope={scope}
          api={api}
          keys={keys}
          typedOnly={props.typedOnly ?? false}
          onSaved={(next) => {
            setNotice(null);
            setEditing(false);
            props.onSaved(next);
          }}
          onRefused={(view) => {
            setNotice(view.message);
            setEditing(false);
            if (view.removeControl) setReadOnly(true);
            if (view.reload) props.onRetry();
          }}
          onCancel={() => setEditing(false)}
        />
      )}
    </li>
  );
}
